import { Logger } from '@elliemae/pui-diagnostics';
import { removeDoubleSlash, isJSDOM, escapeRegExp } from '../utils.js';
import { partitionScriptsByOrigin } from '../utils/script-origin.js';
import { syncWebpackPublicPath } from '../utils/webpack-public-path.js';

export const APP_SCRIPT_ID_PREFIX = 'ice-script-';
// const HEAD_SCRIPTS = /(?:emuiDiagnostics|global|global-prod|emuiUserMonitoring)(?:..*)?.js/;

// const isDeferEligible = (scriptSrc: string) => !HEAD_SCRIPTS.test(scriptSrc);
// const isHeadScript = (scriptSrc: string) => HEAD_SCRIPTS.test(scriptSrc);

type LoadOptions = {
  name: string;
  hostUrl: string;
  documentEle: Document;
  isESMModule?: boolean;
};

type OrderedModuleScriptOptions = {
  scripts: { id: string; href: string }[];
  documentEle: Document;
  isESMModule?: boolean;
  hostUrl?: string;
};

/**
 * Class to load and unload script resources dynamically
 */
/**
 * A utility class for dynamically loading, managing, and removing script elements in the DOM.
 * @remarks
 * This class provides methods to:
 * - Add script elements dynamically to the document
 * - Remove script elements by ID
 * - Clean up dynamically imported scripts by host URL
 * - Remove prefetch link elements by host URL
 * @example
 * ```typescript
 * const logger = new Logger();
 * const scriptLoader = new ScriptLoader(logger);
 *
 * // Add a script
 * await scriptLoader.add({
 *   name: 'myApp',
 *   hostUrl: 'https://example.com',
 *   documentEle: document,
 *   fileName: 'bundle.js',
 *   index: 0
 * });
 *
 * // Remove a script
 * await scriptLoader.remove('app-script-myapp-0', document);
 * ```
 */
export class ScriptLoader {
  #logger: Logger;

  constructor(logger: Logger) {
    this.#logger = logger;
  }

  /**
   * Creates a modulepreload link element for performance optimization.
   * @param href.href
   * @param href - The URL of the script to preload
   * @param href.isESMModule
   * @param href.documentEle
   * @remarks
   * Modulepreload hints allow the browser to fetch and cache the module script
   * earlier in the page load lifecycle, improving performance.
   * The crossorigin attribute is set to 'anonymous' to match the script loading behavior
   * and prevent CORS issues with cross-origin resources.
   * @private
   */
  #preLoadScript = ({
    href,
    isESMModule = true,
    documentEle,
  }: {
    href: string;
    isESMModule?: boolean;
    documentEle: Document;
  }) => {
    const link = documentEle.createElement('link');
    link.rel = isESMModule ? 'modulepreload' : 'preload';
    link.href = href;
    link.crossOrigin = 'anonymous';
    if (!isESMModule) {
      link.as = 'script';
    }
    documentEle.head.appendChild(link);
  };

  /**
   * Creates a script element configured for ordered dynamic loading.
   * @param root0
   * @param root0.id
   * @param root0.documentEle
   * @param root0.isESMModule
   */
  #createScriptElement = ({
    id,
    documentEle,
    isESMModule = true,
  }: {
    id: string;
    documentEle: Document;
    isESMModule?: boolean;
  }): HTMLScriptElement => {
    const ele = documentEle.createElement('script');
    ele.id = id;
    ele.crossOrigin = 'anonymous';
    ele.nonce = '__CSP_NONCE__';
    if (!isJSDOM() && isESMModule) ele.type = 'module';
    // Dynamically created scripts are force-async; disable so execution follows injection order.
    ele.async = false;
    return ele;
  };

  #bindScriptLoadHandlers = (
    ele: HTMLScriptElement,
    href: string,
  ): Promise<string> =>
    new Promise((resolve, reject) => {
      ele.onload = resolve.bind(null, ele.id);
      ele.onerror = (err) => {
        this.#logger.error({
          message: `Error loading script ${ele.src}`,
          exception: new Error(err as string),
        });
        reject(
          new Error(`Unable to load script ${ele.src}`, {
            cause: new Error(err as string),
          }),
        );
      };
      ele.src = removeDoubleSlash(href);
    });

  /**
   * Appends script elements in document order, then assigns src so every tag
   * is in the DOM before any fetch can start (preserves injection order even
   * when earlier scripts are cached).
   * @param entries
   * @param documentEle
   * @param isESMModule
   */
  #loadAndWaitForScripts = async (
    entries: { id: string; href: string }[],
    documentEle: Document,
    isESMModule?: boolean,
  ): Promise<void> => {
    if (entries.length === 0) return;

    const scriptElements = entries.map(({ id }) =>
      this.#createScriptElement({ id, documentEle, isESMModule }),
    );

    scriptElements.forEach((ele) => {
      documentEle.head.appendChild(ele);
    });

    const promises = entries.map(({ href }, index) =>
      this.#bindScriptLoadHandlers(scriptElements[index]!, href),
    );
    await Promise.all(promises);
  };

  /**
   * Stages external scripts before local guest-host scripts.
   * @param root0
   * @param root0.scripts
   * @param root0.documentEle
   * @param root0.isESMModule
   * @param root0.hostUrl
   */
  #loadOrderedModuleScripts = async ({
    scripts,
    documentEle,
    isESMModule,
    hostUrl,
  }: OrderedModuleScriptOptions): Promise<void> => {
    if (scripts.length === 0) return;

    if (!hostUrl) {
      await this.#loadAndWaitForScripts(scripts, documentEle, isESMModule);
      return;
    }

    const { external, local } = partitionScriptsByOrigin(scripts, hostUrl);

    await this.#loadAndWaitForScripts(external, documentEle, isESMModule);
    syncWebpackPublicPath(documentEle.defaultView, hostUrl);
    await this.#loadAndWaitForScripts(local, documentEle, isESMModule);
  };

  /**
   * Loads multiple script assets in manifest order, like HTML module script tags.
   * @param assets - Array of script URLs (either full HTTP/HTTPS URLs or relative paths)
   * @param options - Configuration options for loading the scripts
   * @param options.name - The name of the application (used for generating script IDs)
   * @param options.hostUrl - The base URL for resolving relative script paths
   * @param options.documentEle - The document object where scripts should be added
   * @param options.isESMModule - Whether scripts should be loaded as ES modules (default: true)
   * @returns A promise that resolves with an array of script element IDs when all scripts are loaded
   * @remarks
   * This method:
   * - Stages external (CDN) scripts before local guest-host scripts
   * - Preserves manifest order within each origin group
   * - Creates modulepreload links for all assets for performance optimization
   * - Inserts script tags synchronously per stage so the browser can fetch in parallel
   * - Syncs webpack public path from hostUrl (or guest _ASSET_PATH) before local scripts run
   * @example
   * ```typescript
   * const scriptIds = await scriptLoader.load(
   *   ['https://cdn.example.com/lib.js', 'global.js', 'runtime~app.js'],
   *   {
   *     name: 'myApp',
   *     hostUrl: 'https://example.com/my-app/',
   *     documentEle: document,
   *     isESMModule: true
   *   }
   * );
   * ```
   */
  load = async (assets: string[], options: LoadOptions) => {
    const { documentEle, isESMModule } = options;
    const httpPattern = /^https?:\/\//i;

    const orderedScripts = assets.map((fileName, index) => {
      const id = `${APP_SCRIPT_ID_PREFIX}${options.name.toLowerCase()}-${index}`;
      const href = httpPattern.test(fileName)
        ? fileName
        : new URL(fileName, options.hostUrl).href;

      this.#preLoadScript({ href, documentEle, isESMModule });
      return { id, href };
    });

    await this.#loadOrderedModuleScripts({
      scripts: orderedScripts,
      documentEle,
      isESMModule,
      hostUrl: options.hostUrl,
    });

    syncWebpackPublicPath(documentEle.defaultView, options.hostUrl);
    return orderedScripts.map((asset) => asset.id);
  };

  /**
   * Removes a script element from the document by its ID.
   * @param elementId - The ID of the script element to remove (default: '')
   * @param documentEle - The document object from which to remove the script (default: document)
   * @returns A promise that resolves when the script element has been removed
   * @remarks
   * If the element with the specified ID is not found, a warning is logged but the promise still resolves.
   * @example
   * ```typescript
   * await scriptLoader.remove('ice-script-myapp-0', document);
   * ```
   */
  remove = (elementId = '', documentEle = document): Promise<void> =>
    new Promise((resolve) => {
      const ele = documentEle.getElementById(elementId) as HTMLScriptElement;
      if (!ele) {
        this.#logger.warn(`script with id ${elementId} not found`);
        resolve();
        return;
      }
      ele.remove();
      resolve();
    });

  /**
   * Removes all script elements from the document that match a specific host URL pattern.
   * @param hostUrl - The host URL pattern to match against script sources
   * @param documentEle - The document object from which to remove scripts
   * @remarks
   * This method iterates through all script elements in reverse order and removes any script
   * whose `src` attribute matches the provided host URL pattern (case-insensitive).
   * This is useful for cleaning up dynamically imported scripts from a specific host.
   * @example
   * ```typescript
   * scriptLoader.removeDynamicImportedScripts('https://cdn.example.com', document);
   * ```
   */
  removeDynamicImportedScripts = (
    hostUrl: string,
    documentEle: Document,
  ): void => {
    const hostPattern = new RegExp(escapeRegExp(hostUrl), 'i');
    const scriptElements = documentEle.getElementsByTagName('script');
    for (let index = scriptElements.length - 1; index >= 0; index -= 1) {
      const scriptEle = scriptElements[index];
      const { src } = scriptEle;
      if (hostPattern.test(src)) scriptEle.remove();
    }
  };

  /**
   * Removes all prefetch link elements from the document that match a specific host URL pattern.
   * @param hostUrl - The host URL pattern to match against link hrefs
   * @param documentEle - The document object from which to remove prefetch links
   * @remarks
   * This method queries all elements with `rel="prefetch"` and removes any link whose `href`
   * attribute matches the provided host URL pattern (case-insensitive). This is useful for
   * cleaning up resource hints that are no longer needed.
   * @example
   * ```typescript
   * scriptLoader.removePrefetchLinks('https://cdn.example.com', document);
   * ```
   */
  removePrefetchLinks = (hostUrl: string, documentEle: Document): void => {
    const hostPattern = new RegExp(escapeRegExp(hostUrl), 'i');
    const prefetchElements = documentEle.querySelectorAll('[rel="prefetch"]');
    for (let index = prefetchElements.length - 1; index >= 0; index -= 1) {
      const ele = prefetchElements[index];
      const { href } = ele as HTMLLinkElement;
      if (hostPattern.test(href)) ele.remove();
    }
  };
}
